<template>
	<Form :validation-schema="validationSchema" class="form-container" @submit="updateDocumentAsync" v-slot="{ errors }">
		<div v-for="field in formFields" :key="field.name" :class="['form-input', field.width]">
			<label :for="field.name">{{ field.label }}</label>
			<Field
				as="select"
				:id="field.name"
				:name="field.name"
				v-model="formData[field.name]"
				:disabled="isSubmitting"
			>
				<option></option>
				<option v-for="option in field.options" :key="option.id" :value="option.id">
					{{ option.name }}
				</option>
			</Field>
			<ErrorMessage :name="field.name" class="error" />
		</div>

		<button
			class="submit-button col-12"
			type="submit"
			:disabled="!isFormValid(errors) || isSubmitting"
		>
			Submit
		</button>
	</Form>
</template>

<script setup lang="ts">
	import { ref, reactive, computed, watchEffect } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import { Form, Field, ErrorMessage } from 'vee-validate';
	import { Borrower, LoanDocument, UpdateDocumentRequest } from '@/models/loans';
	import { useFormFields } from '@/composables/loan/documents/useEditDocumentForm';
	import { useDocumentData } from '@/composables/loan/documents/useDocumentData';

	const props = defineProps<{
		document: LoanDocument;
		borrowers: Borrower[];
		onDocumentChangedAsync: () => Promise<void>;
	}>();

	const isSubmitting = ref<boolean>(false);

	const updateDocumentRequest = ref<UpdateDocumentRequest>({
		documentTypeId: props.document.documentTypeId,
		borrowerId: props.document.borrowerId
	});

	const { documentTypeOptions } = useDocumentData();

	const { formFields, validationSchema } = useFormFields({
		document: updateDocumentRequest,
		borrowers: props.borrowers,
		documentTypeOptions
	});

	const formData = reactive<Record<string, any>>({});

	const isFormValid = computed(() => {
		return (errors: Partial<Record<string, string | undefined>>) => errors && Object.keys(errors).length === 0;
	});

	async function updateDocumentAsync(formData: Record<string, any>) {
		isSubmitting.value = true;

		try {
			const request = {
				id: props.document.id,
				...formData
			};

			await axios.post(`/api/loandocuments/${props.document.id}`, request);

			await props.onDocumentChangedAsync();
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue updating the document. Please try again later.'
			});
		} finally {
			isSubmitting.value = false;
		}
	}

	watchEffect(() => {
		formFields.value.forEach((field) => {
			formData[field.name] = field.value;
		});
	});
</script>

<style scoped src="@/assets/css/loans/form.css"></style>
